/**
 * Shop Entry: the catalogue, the stock ledger, and recording a sale at the
 * point it happens.
 *
 * The organising constraint, stated once here because everything else follows
 * from it: **a Shop Entry sale is an ordinary CenoX sale**. It creates a row in
 * `sales` through the same service every other sale goes through, so every
 * report, breakdown, drill-down and export picks it up with no new code. There
 * is deliberately no parallel ledger and no second reporting path — the moment
 * one exists, the dashboard stops being true.
 */

import { z } from 'zod';
import {
  isoDateTimeSchema,
  longTextSchema,
  nonNegativeMoneySchema,
  paginationSchema,
  shortTextSchema,
  sortOrderSchema,
  uuidSchema,
} from '../primitives.js';
import {
  PAYMENT_METHODS,
  PAYMENT_STATUSES,
  PRODUCT_AVAILABILITIES,
  PRODUCT_KINDS,
  SALE_SOURCES,
  STOCK_MOVEMENT_KINDS,
} from '../enums.js';

// ── Catalogue categories ────────────────────────────────────────────────────

export const createProductCategorySchema = z.object({
  name: shortTextSchema,
  description: longTextSchema.optional(),
});
export type CreateProductCategoryInput = z.infer<typeof createProductCategorySchema>;

export const updateProductCategorySchema = createProductCategorySchema.partial();
export type UpdateProductCategoryInput = z.infer<typeof updateProductCategorySchema>;

export const productCategorySchema = z.object({
  id: uuidSchema,
  name: z.string(),
  description: z.string().nullable(),
  /** Seeded from the business template. Renameable, and deletable when unused. */
  isSystem: z.boolean(),
  productCount: z.number(),
  createdAt: z.string(),
});
export type ProductCategory = z.infer<typeof productCategorySchema>;

// ── Catalogue entries ───────────────────────────────────────────────────────

/**
 * A barcode as it will be stored and matched.
 *
 * Normalised on the way in — trimmed, upper-cased — because scanners and
 * typists disagree about whitespace and case, and a barcode that fails to
 * match on a trailing space is indistinguishable, to the person holding the
 * carton, from one that was never registered. Empty becomes null rather than
 * an empty string, so "no barcode" is one value and not two.
 */
export const barcodeSchema = z
  .string()
  .trim()
  .max(64)
  .transform((value) => value.toUpperCase())
  .refine((value) => value === '' || /^[A-Z0-9][A-Z0-9._\/-]*$/.test(value), {
    message: 'A barcode can contain letters, digits, dots, dashes and slashes',
  })
  .transform((value) => (value === '' ? null : value));


/**
 * `trackStock` is independent of `kind` rather than derived from it. A service
 * business may still sell a bag of coffee, and a product business may sell an
 * installation it never counts. Defaulting from the template and letting the
 * owner override per item is the only arrangement that fits both.
 */
export const createCatalogueItemSchema = z
  .object({
    name: shortTextSchema,
    kind: z.enum(PRODUCT_KINDS).default('product'),
    sku: z.string().trim().max(60).nullable().optional(),
    /** What a scanner reads off the carton. Unique within the business. */
    barcode: barcodeSchema.nullable().optional(),
    description: longTextSchema.nullable().optional(),
    /** What the customer pays. Optional so a price-on-request item can exist. */
    unitPrice: nonNegativeMoneySchema.nullable().optional(),
    /** What it costs you. Drives gross profit when a sale carries it. */
    costPrice: nonNegativeMoneySchema.nullable().optional(),
    categoryId: uuidSchema.nullable().optional(),
    branchId: uuidSchema.nullable().optional(),
    availability: z.enum(PRODUCT_AVAILABILITIES).default('available'),
    trackStock: z.boolean().default(false),
    /** Opening stock, recorded as an `opening` movement rather than a column. */
    openingStock: z.coerce.number().int().min(0).optional(),
    /**
     * Where the opening stock is standing.
     *
     * Required only when there IS opening stock — a catalogue entry with no
     * balance yet belongs to the business and needs no location. Separate from
     * `branchId`, which says the item is only SOLD at one location: that is a
     * statement about the catalogue, not about which shelf holds the goods.
     */
    openingStockBranchId: uuidSchema.nullable().optional(),
    /** Below this, the catalogue flags the item as running low. */
    reorderLevel: z.coerce.number().int().min(0).nullable().optional(),
    isActive: z.boolean().default(true),
  })
  .refine((item) => item.kind === 'product' || !item.trackStock, {
    message: 'A service has no stock to track',
    path: ['trackStock'],
  });
export type CreateCatalogueItemInput = z.infer<typeof createCatalogueItemSchema>;

export const updateCatalogueItemSchema = z.object({
  name: shortTextSchema.optional(),
  kind: z.enum(PRODUCT_KINDS).optional(),
  sku: z.string().trim().max(60).nullable().optional(),
  barcode: barcodeSchema.nullable().optional(),
  description: longTextSchema.nullable().optional(),
  unitPrice: nonNegativeMoneySchema.nullable().optional(),
  costPrice: nonNegativeMoneySchema.nullable().optional(),
  categoryId: uuidSchema.nullable().optional(),
  branchId: uuidSchema.nullable().optional(),
  availability: z.enum(PRODUCT_AVAILABILITIES).optional(),
  trackStock: z.boolean().optional(),
  reorderLevel: z.coerce.number().int().min(0).nullable().optional(),
  isActive: z.boolean().optional(),
});
export type UpdateCatalogueItemInput = z.infer<typeof updateCatalogueItemSchema>;

export const catalogueItemSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  kind: z.enum(PRODUCT_KINDS),
  sku: z.string().nullable(),
  barcode: z.string().nullable(),
  description: z.string().nullable(),
  unitPrice: z.string().nullable(),
  costPrice: z.string().nullable(),
  /**
   * Weighted average of what stock actually cost, from posted receipts.
   *
   * Separate from `costPrice`, which is what the owner typed. Null until a
   * delivery with a stated cost has been posted — and null is not zero, so the
   * screen must not render them the same way.
   */
  averageCost: z.string().nullable(),
  categoryId: uuidSchema.nullable(),
  categoryName: z.string().nullable(),
  branchId: uuidSchema.nullable(),
  branchName: z.string().nullable(),
  availability: z.enum(PRODUCT_AVAILABILITIES),
  trackStock: z.boolean(),
  reorderLevel: z.number().nullable(),
  isActive: z.boolean(),
  /**
   * Derived from the movement ledger at read time, never stored.
   *
   * Null when the item does not track stock — which is a different statement
   * from zero, and the UI must not render them the same way.
   */
  onHand: z.number().nullable(),
  /** True when `onHand` has fallen to or below `reorderLevel`. */
  isLow: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CatalogueItem = z.infer<typeof catalogueItemSchema>;

export const catalogueFiltersSchema = paginationSchema.extend({
  search: z.string().trim().max(120).optional(),
  kind: z.enum(PRODUCT_KINDS).optional(),
  categoryId: uuidSchema.optional(),
  branchId: uuidSchema.optional(),
  availability: z.enum(PRODUCT_AVAILABILITIES).optional(),
  /** Only items at or below their reorder level. */
  lowStockOnly: z.coerce.boolean().optional(),
  includeInactive: z.coerce.boolean().optional(),
  sortBy: z.enum(['name', 'createdAt', 'unitPrice']).default('name'),
  sortOrder: sortOrderSchema,
});
export type CatalogueFilters = z.infer<typeof catalogueFiltersSchema>;

// ── Stock ledger ────────────────────────────────────────────────────────────

/**
 * Stock is a ledger, not a counter.
 *
 * On-hand is the sum of every movement, computed at read time — the same
 * reasoning that makes `sale_payments` a table rather than an `amount_paid`
 * column. A stored quantity that drifts from the events that produced it is
 * worse than no quantity at all, because it looks authoritative.
 *
 * `quantity` is signed: positive for receipts and returns, negative for sales
 * and wastage. The sign is derived from `kind` on the server, so a client
 * cannot record a receipt that secretly depletes stock.
 */
export const recordStockMovementSchema = z.object({
  productId: uuidSchema,
  kind: z.enum(STOCK_MOVEMENT_KINDS),
  /** Always positive. The server applies the sign implied by `kind`. */
  quantity: z.coerce.number().int().positive(),
  /** Unit cost for a receipt, so stock value can be reported later. */
  unitCost: nonNegativeMoneySchema.nullable().optional(),
  branchId: uuidSchema.nullable().optional(),
  note: longTextSchema.nullable().optional(),
  occurredAt: isoDateTimeSchema.optional(),
});
export type RecordStockMovementInput = z.infer<typeof recordStockMovementSchema>;

export const stockMovementSchema = z.object({
  id: uuidSchema,
  productId: uuidSchema,
  productName: z.string().nullable(),
  kind: z.enum(STOCK_MOVEMENT_KINDS),
  /** Signed. Negative entries reduced stock. */
  quantity: z.number(),
  unitCost: z.string().nullable(),
  branchId: uuidSchema.nullable(),
  branchName: z.string().nullable(),
  /** The sale that caused this movement, when it was caused by one. */
  saleId: uuidSchema.nullable(),
  saleReference: z.string().nullable(),
  note: z.string().nullable(),
  occurredAt: z.string(),
  recordedByName: z.string().nullable(),
  createdAt: z.string(),
});
export type StockMovement = z.infer<typeof stockMovementSchema>;

/**
 * Moving stock between two locations.
 *
 * Both ends are required and must differ. There is no "from the business" —
 * stock always sits somewhere, and a move that named only its destination
 * would be a receipt wearing another word.
 */
export const transferStockSchema = z.object({
  productId: uuidSchema,
  /** Always positive. The server writes the negative leg itself. */
  quantity: z.coerce.number().int().positive(),
  fromBranchId: uuidSchema,
  toBranchId: uuidSchema,
  /** Carried onto both legs, so the receiving location sees what it is worth. */
  unitCost: nonNegativeMoneySchema.nullable().optional(),
  note: longTextSchema.nullable().optional(),
  occurredAt: isoDateTimeSchema.optional(),
});
export type TransferStockInput = z.infer<typeof transferStockSchema>;

export const stockFiltersSchema = paginationSchema.extend({
  productId: uuidSchema.optional(),
  branchId: uuidSchema.optional(),
  kind: z.enum(STOCK_MOVEMENT_KINDS).optional(),
});
export type StockFilters = z.infer<typeof stockFiltersSchema>;

// ── Shop Entry: recording a sale ────────────────────────────────────────────

/**
 * One line of a shop sale.
 *
 * `productId` is optional so the till can take an ad-hoc amount — a haircut
 * that is not on the price list, a one-off delivery charge — without forcing
 * the owner to create a catalogue entry mid-transaction. That is the single
 * most common reason a point-of-sale gets abandoned for a paper book.
 */
export const shopSaleLineSchema = z.object({
  productId: uuidSchema.nullable().optional(),
  /** Falls back to the catalogue price when omitted and a product is named. */
  unitPrice: nonNegativeMoneySchema.optional(),
  quantity: z.coerce.number().int().positive().default(1),
  /** Required only when there is no product to take a name from. */
  description: shortTextSchema.optional(),
});
export type ShopSaleLine = z.infer<typeof shopSaleLineSchema>;

/**
 * A sale recorded from the shop floor.
 *
 * Phase 1 records **one line per sale**, matching the `sales` table's single
 * amount, which is what keeps `revenue = sum(sales.amount)` exactly true and
 * every existing reconciliation untouched. The schema is shaped as a line
 * rather than loose fields so multi-line baskets can arrive later without the
 * client contract changing shape.
 */
export const saleItemSchema = z.object({
  id: uuidSchema,
  productId: uuidSchema.nullable(),
  productName: z.string().nullable(),
  description: z.string().nullable(),
  quantity: z.number(),
  unitPrice: z.string(),
  unitCost: z.string().nullable(),
  lineTotal: z.string(),
});
export type SaleItem = z.infer<typeof saleItemSchema>;

/** A line on the way in: by product id, or by the barcode a scanner read. */
export const basketLineSchema = shopSaleLineSchema.extend({
  barcode: barcodeSchema.optional(),
});
export type BasketLine = z.infer<typeof basketLineSchema>;

export const createShopSaleSchema = z.object({
  /**
   * The basket.
   *
   * `line` is the original single-item shape and still works untouched;
   * `lines` is the basket. Exactly one of them, because a request carrying
   * both is ambiguous about what was actually sold and guessing is how a till
   * records the wrong money.
   */
  lines: z.array(basketLineSchema).min(1).max(200).optional(),
  line: shopSaleLineSchema.optional(),

  /**
   * De-duplication key for the checkout.
   *
   * A till on a bad connection retries, and a doubled checkout takes money
   * twice and deducts stock twice. Threaded to `sales.external_ref`, which
   * already carries a per-tenant unique index.
   */
  idempotencyKey: z.string().trim().min(8).max(120).optional(),

  /** Named customer, or nothing at all for walk-in trade. */
  customerId: uuidSchema.nullable().optional(),
  /**
   * Create a customer as part of the sale.
   *
   * The till is where a customer's details are actually captured — asking the
   * owner to leave the sale, create a customer and come back is how walk-ins
   * end up unattributed forever.
   */
  newCustomer: z
    .object({
      name: shortTextSchema,
      phone: z.string().trim().max(32).optional(),
      email: z.string().trim().max(200).optional(),
    })
    .optional(),

  branchId: uuidSchema.nullable().optional(),
  staffId: uuidSchema.nullable().optional(),
  channelId: uuidSchema.nullable().optional(),
  source: z.enum(SALE_SOURCES).default('pos'),

  paymentMethod: z.enum(PAYMENT_METHODS).default('cash'),
  paymentStatus: z.enum(PAYMENT_STATUSES).default('paid'),
  /** Part payment at the till. Defaults to the full amount when status is paid. */
  amountPaid: nonNegativeMoneySchema.optional(),
  /**
   * In a pharmacy: the cashier has seen the card slip or the bank alert for
   * the money taken now. Required there for any non-cash payment; ignored by
   * every other business, whose "Paid" means what it always has.
   */
  paymentConfirmed: z.boolean().optional(),
  /** The reference on the bank alert. Required in a pharmacy for a transfer or mobile money. */
  paymentReference: z.string().trim().max(120).optional(),

  notes: longTextSchema.optional(),
  occurredAt: isoDateTimeSchema.optional(),
})
  .refine((body) => Boolean(body.lines) !== Boolean(body.line), {
    message: 'Send either a single line or a basket, not both',
    path: ['lines'],
  });
export type CreateShopSaleInput = z.infer<typeof createShopSaleSchema>;

/**
 * A warning is advice, not a failure.
 *
 * Selling past zero is allowed by default because stock counts drift in every
 * real shop, and a till that refuses the sale is a till that gets bypassed.
 * The warning is returned so the UI can say so plainly at the moment it
 * matters, and the sale is still recorded.
 */
export const shopSaleWarningSchema = z.object({
  code: z.enum([
    'stock_would_go_negative',
    'item_unavailable',
    'no_price_on_file',
    // A retry that matched an existing checkout. Reported rather than hidden:
    // the cashier needs to know the customer was not charged twice.
    'duplicate_ignored',
  ]),
  message: z.string(),
  productId: uuidSchema.nullable().optional(),
});
export type ShopSaleWarning = z.infer<typeof shopSaleWarningSchema>;

export const shopSaleResultSchema = z.object({
  saleId: uuidSchema,
  reference: z.string(),
  amount: z.string(),
  currencyCode: z.string(),
  customerName: z.string().nullable(),
  /** On-hand after the sale, when the item tracks stock. */
  remainingStock: z.number().nullable(),
  warnings: z.array(shopSaleWarningSchema),
});
export type ShopSaleResult = z.infer<typeof shopSaleResultSchema>;

/** Everything the till needs to render, in one request. */
export const shopContextSchema = z.object({
  currencyCode: z.string(),
  timezone: z.string(),
  catalogueNoun: z.object({ singular: z.string(), plural: z.string() }),
  stockPolicy: z.enum(['warn', 'block', 'ignore']),
  items: z.array(catalogueItemSchema),
  categories: z.array(productCategorySchema),
  branches: z.array(z.object({ id: uuidSchema, name: z.string(), isDefault: z.boolean() })),
  staff: z.array(z.object({ id: uuidSchema, fullName: z.string() })),
  // `slug` travels with the channel so the till can identify the counter by
  // meaning rather than by list position. Names are user-editable; slugs are
  // the stable identity the seeded channels are created with.
  channels: z.array(z.object({ id: uuidSchema, name: z.string(), slug: z.string() })),
});
export type ShopContext = z.infer<typeof shopContextSchema>;

// ── Till search: one answer to "can this be sold, and if not, why" ──────────

/**
 * What a till shows beside an item.
 *
 * Every matching catalogue item is shown — out of stock, unclassified or
 * discontinued included — so a cashier who searches for something the
 * business carries finds it and learns why it cannot go in the basket, rather
 * than being told it does not exist. Only `available` may be added; the server
 * applies the same rules again at checkout.
 */
export const TILL_ITEM_STATES = [
  'available',
  'out_of_stock',
  'awaiting_classification',
  'discontinued',
  'prescription_required',
  'pharmacist_only',
  'restricted',
  'pharmacy_till_only',
  'no_price',
] as const;
export type TillItemState = (typeof TILL_ITEM_STATES)[number];

export const TILL_ITEM_STATE_LABELS: Record<TillItemState, string> = {
  available: 'Available',
  out_of_stock: 'Out of stock',
  awaiting_classification: 'Awaiting classification',
  discontinued: 'Discontinued',
  prescription_required: 'Prescription required',
  pharmacist_only: 'Pharmacist sale only',
  restricted: 'Restricted',
  pharmacy_till_only: 'Sell at the pharmacy till',
  no_price: 'No price',
};

/** A search by words, or an exact barcode from a scanner — never both. */
export const tillItemSearchSchema = z
  .object({
    search: z.string().trim().min(1).max(120).optional(),
    barcode: z.string().trim().min(1).max(64).optional(),
    branchId: uuidSchema.optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
  })
  .refine((q) => Boolean(q.search) !== Boolean(q.barcode), { message: 'Give a search or a barcode, not both' });
export type TillItemSearch = z.infer<typeof tillItemSearchSchema>;

/**
 * Whether a miss on Enter should be reported as an unknown barcode.
 *
 * Only an all-digit code of retail-barcode length is: "PERFUME" is a word
 * somebody typed, not a scan, and is answered by the name search instead.
 * A scanned code with letters is still looked up exactly; it simply does not
 * produce a "no such barcode" message when nothing carries it.
 */
export function isNumericBarcode(raw: string): boolean {
  return /^\d{6,14}$/.test(raw.trim());
}

/** Whether Enter should try an exact barcode lookup at all: one token, no spaces. */
export function couldBeBarcode(raw: string): boolean {
  return /^[A-Za-z0-9-]{4,64}$/.test(raw.trim());
}
